import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireCheckoutSession?: boolean;
}

/**
 * Protected route component that enforces access rules
 * Updated to work with the new cart operation system and cart structure
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireCheckoutSession = true,
}) => {
  const location = useLocation();

  // Get cart state from Redux
  const cart = useSelector((state: any) => state.cart);

  // Check for valid checkout session in sessionStorage
  const checkoutSession = sessionStorage.getItem("checkoutSession");
  const reservationExpiry = sessionStorage.getItem("checkoutReservationExpiry");

  // Check if we have a valid checkout session
  const isSessionValid = () => {
    // If checkout session is not required, return true
    if (!requireCheckoutSession) return true;

    // Check if checkout session exists
    if (!checkoutSession) {
      console.warn("❌ No checkout session found in sessionStorage");
      return false;
    }

    // Check if reservation expiry exists and is still valid
    // This works with the combined validate-and-reserve API response format
    if (reservationExpiry) {
      const expiryTime = new Date(reservationExpiry).getTime();
      const currentTime = new Date().getTime();

      if (expiryTime < currentTime) {
        console.warn("❌ Checkout reservation has expired");
        return false;
      }
    }

    return true;
  };

  // Check if cart data is valid
  const isCartValid = () => {
    // If checkout session is not required, return true
    if (!requireCheckoutSession) return true;

    // Check if cart exists and has groups with products
    if (!cart || !cart.cartId || !cart.groups || cart.groups.length === 0) {
      console.warn("❌ Invalid cart data in Redux store");
      return false;
    }

    // Verify all groups have valid delivery dates
    const allGroupsHaveDates = cart.groups.every(
      (group: any) => !!group.deliveryDate
    );
    if (!allGroupsHaveDates) {
      console.warn("❌ Some cart groups are missing delivery dates");
      return false;
    }

    // Verify all groups have valid delivery fees (not undefined, null, or -1)
    const allGroupsHaveValidFees = cart.groups.every((group: any) => {
      const fee = group.deliveryFee;
      return fee !== undefined && fee !== null && fee !== -1;
    });
    if (!allGroupsHaveValidFees) {
      console.warn("❌ Some cart groups have invalid delivery fees");
      return false;
    }

    return true;
  };

  // Determine if access should be allowed
  const canAccess = isSessionValid() && isCartValid();

  // Log reason for redirect if access is denied
  useEffect(() => {
    if (!canAccess) {
      console.error("❌ Access denied to protected route:", {
        path: location.pathname,
        hasCheckoutSession: !!checkoutSession,
        hasReservationExpiry: !!reservationExpiry,
        hasCartId: !!cart?.cartId,
        groupsCount: cart?.groups?.length || 0,
        operations: {
          locationUpdate: cart?.operations?.locationUpdate?.inProgress || false,
          feeCalculation: cart?.operations?.feeCalculation?.inProgress || false,
          dateUpdate: cart?.operations?.dateUpdate?.inProgress || false,
          itemUpdate: cart?.operations?.itemUpdate?.inProgress || false,
        },
      });
    }
  }, [canAccess, location.pathname, checkoutSession, cart]);

  // Redirect to cart if access is denied
  if (!canAccess) {
    return <Navigate to="/cart" replace state={{ accessDenied: true }} />;
  }

  // Render children if access is allowed
  return <>{children}</>;
};

export default ProtectedRoute;
